import React from "react";

import {
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  FormDataConsumer,
  ArrayInput,
  SimpleFormIterator,
  TextInput,
  required,
  minLength,
  maxLength,
  regex,
  email,
  SelectInput,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  InternalAccountIcon,
  BrokeredAccountIcon,
  OpenInNewIcon,
} from "../../../components/common/iconography";
import { ColorInput } from "../../../components/common/ColorInput";
import { AddressInput } from "../../../components/common/AddressInput";

import {
  AccountType,
  AccountTypeChoices,
  REGEX_MULTI_EMAIL,
  REGEX_PHONE,
  REGEX_URL,
} from "../../../utils/constants";
import {
  normalizeWebsiteUrl,
  validateAccounNameUnicity,
  validateLotPrefixUnique,
} from "./AccountUtils";
import { SmartTipIcon } from "../../../components/common/SmartTipIcon";
import { AccountDropDown } from "../../../components/common/AccountDropDown";

const useStyles = makeStyles((theme) => ({
  arrayInput: {
    marginTop: theme.spacing(-2),
  },
}));

const validateMeta = (value, allValues, meta) => {
  if (!value) return undefined;
  return meta?.data?.error || undefined;
};

const validateAccountName = [required(), validateMeta];

const validateRequired = required();
const validateAccountEmail = [
  required(),
  regex(REGEX_MULTI_EMAIL, "Must be valid email(s)"),
];
const validateLotNoPrefix = [
  required(),
  minLength(2, "Must be 1 characters at least"),
  validateLotPrefixUnique,
]; // min 2 cause contains '-'
const validatePhone = [
  required(),
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];
const validateOptEmail = [email()];
const validateOptPhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];
const validateWebsite = [regex(REGEX_URL, "Must be valid URL")];

const isCreateForm = (record) => {
  return Object.keys(record).length === 0;
};

export const ContactTab = ({ record, ...props }) => {
  const form = useForm();
  const classes = useStyles();

  const getExternalAccountNumberHelperText = () => {
    return record?.externalId?.trim()
      ? `External Account Number: ${record.externalId}`
      : false;
  };

  const handleBusinessNameChange = (event) => {
    form.change("billingAddress.name", event.target.value);
  };

  const handleAccountTypeChange = (event) => {
    const accountType = event.target.value;
    if (accountType === AccountType.BROKERED) {
      form.change("skipInvoice", true);
    } else if (accountType === AccountType.CUSTOMER) {
      form.change("skipInvoice", false);
      form.change("defaultBillToAccountId", null);
    } else {
      // INTERNAL
      form.change("skipInvoice", true);
      form.change("defaultBillToAccountId", null);
    }
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container direction="column" spacing={1}>
          <Grid container item direction="row" spacing={2}>
            <Grid item xs={12} md={3}>
              {isCreateForm(record) ? (
                <TextInput
                  source="businessName"
                  label="Account Name"
                  variant="outlined"
                  fullWidth
                  onChange={handleBusinessNameChange}
                  validate={validateAccountName}
                  onBlur={async () => {
                    await validateAccounNameUnicity(
                      "businessName",
                      form,
                      formData,
                    );
                  }}
                  helperText={false}
                  InputProps={{
                    endAdornment: formData.accountType !==
                      AccountType.CUSTOMER && (
                      <InputAdornment position="end">
                        {formData.accountType === AccountType.INTERNAL ? (
                          <InternalAccountIcon />
                        ) : formData.accountType === AccountType.BROKERED ? (
                          <BrokeredAccountIcon />
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <TextInput
                  source="businessName"
                  label="Account Name"
                  variant="outlined"
                  fullWidth
                  validate={validateAccountName}
                  onBlur={() =>
                    validateAccounNameUnicity("businessName", form, formData)
                  }
                  helperText={getExternalAccountNumberHelperText()}
                  InputProps={{
                    endAdornment: record.accountType !==
                      AccountType.CUSTOMER && (
                      <InputAdornment position="end">
                        {record.accountType === AccountType.INTERNAL ? (
                          <Tooltip title="Internal Account" arrow>
                            <InternalAccountIcon />
                          </Tooltip>
                        ) : record.accountType === AccountType.BROKERED ? (
                          <Tooltip title="Brokered Account" arrow>
                            <BrokeredAccountIcon />
                          </Tooltip>
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <TextInput
                source="displayName"
                label="Display Name"
                variant="outlined"
                fullWidth
                validate={validateMeta}
                onBlur={() =>
                  validateAccounNameUnicity("displayName", form, formData)
                }
                helperText={false}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextInput
                source="lotNumberPrefix"
                label="Lot No. Prefix"
                variant="outlined"
                fullWidth
                format={(v) => (v ? v.slice(0, -1) : undefined)}
                parse={(v) =>
                  v ? v.toUpperCase().replace(/\s+/g, "") + "-" : undefined
                }
                initialValue={"-"}
                validate={validateLotNoPrefix}
                helperText={false}
                inputProps={{
                  maxLength: 3,
                  style: { textAlign: "right", textTransform: "uppercase" },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">—</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <ColorInput source="color" label="Account Color" />
            </Grid>
          </Grid>
          <Grid container item direction="row" spacing={2}>
            <Grid item xs={6} md={3}>
              {/*
internal: cement
customer: ReadyMix
    customer: Concrete Results

    inbound: (regular inbound)
      - account: cement
      - operatingAccount = account: cement
      - billToAccount = account: cement
        - inventory cement
        - accountName: cement
        - lotPrefix: cement
        - product: cement
        - pod: cement
    outbound:
      - account: cement
        - inventory: from cement
      - operating account: ReadyMix
      - billToAccount: ReadyMix
        - accountName: ReadyMix
        - pos: ReadyMix


internal: Structural LLC
    customer: Tolko
    customer: SouthBeach

    inbound: (regular inbound)
      - account: Tolko
      - operatingAccount = account: Tolko
      - billToAccount = account: Tolko
        - inventory: Tolko
        - accountName: Tolko
        - lotPrefix: Tolko
        - product: Tolko
        - pod: Tolko
    Ext Transfer:
      - account: Tolko
      - operatingAccount = account: Tolko
      - billToAccount = account: Tolko
        - inventory: from Tolko
        - accountName: Tolko
        - pos: Tolko
      - toAccount: Structural LLC


brokered: Omya
  billToAccount: Lotz
brokered: Flux
  defaultBillToAccount: Lotz

  inbound:
    - account: Omya
    - operatingAccount = account: Omya
      - inventory: Omya
      - accountName: Omya
      - lotPrefix: Omya
      - product: Omya
      - pod: Omya
    - billToAccount: Lotz
  outbound:
    - account: omya
    - operatingAccount = account: Omya
      - inventory from Omya
      - accountName: Omya
      - pos: Omya
    - billToAccount: Lotz
*/}
              <SelectInput
                source="accountType"
                label="Account Type"
                choices={AccountTypeChoices}
                defaultValue={AccountType.CUSTOMER}
                validate={validateRequired}
                allowEmpty={false}
                onChange={handleAccountTypeChange}
                variant="outlined"
                fullWidth
                helperText={false}
              />
              <SmartTipIcon label="account-type-icon" mt={-1} />
            </Grid>
            {formData.accountType === AccountType.BROKERED && (
              <Grid item xs={6} md={3}>
                <AccountDropDown
                  source="defaultBillToAccountId"
                  label="Broker Account"
                  excludeAccountIds={formData.id ? [formData.id] : []}
                  validate={validateRequired}
                  allowEmpty={false}
                  helperText={false}
                />
                <SmartTipIcon
                  label="passthrough-billing-account-icon"
                  mt={-1}
                />
              </Grid>
            )}
          </Grid>

          <Grid container item direction="row" spacing={2} lg={12}>
            <Grid item xs={12} md={3}>
              <TextInput
                source="email"
                type="email"
                variant="outlined"
                fullWidth
                parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                validate={validateAccountEmail}
                helperText={false}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextInput
                source="phone"
                type="tel"
                variant="outlined"
                fullWidth
                validate={validatePhone}
                helperText={false}
              />
            </Grid>
            <Hidden smDown>
              <Grid item xs={false} md={3}>
                <TextInput
                  source="fax"
                  type="tel"
                  variant="outlined"
                  fullWidth
                  validate={validateOptPhone}
                  helperText={false}
                />
              </Grid>
            </Hidden>
          </Grid>
          <Hidden smDown>
            <Grid container item direction="row" spacing={2} lg={12}>
              <Grid item xs={false} md={3}>
                <TextInput
                  source="website"
                  type="url"
                  variant="outlined"
                  fullWidth
                  validate={validateWebsite}
                  helperText={false}
                  InputProps={{
                    endAdornment: (() => {
                      if (!formData.website) return null;
                      return (
                        <InputAdornment position="end">
                          <Tooltip title="Open website" arrow>
                            <IconButton
                              component="a"
                              href={normalizeWebsiteUrl(formData.website)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      );
                    })(),
                  }}
                />
              </Grid>
            </Grid>
          </Hidden>
          <Grid container item direction="row" spacing={2} lg={12}>
            <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="textSecondary">
                    Business Address
                  </Typography>
                </legend>
                <AddressInput
                  source="address"
                  variant="outlined"
                  fullWidth
                  parse={(v) => (v !== "" ? v : undefined)}
                  allValues={formData}
                />
              </fieldset>
            </Grid>
          </Grid>
          {formData.id && (
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      CONTACTS
                    </Typography>
                  </legend>
                  <ArrayInput
                    source="contacts"
                    label=""
                    fullWidth
                    className={classes.arrayInput}
                  >
                    <SimpleFormIterator>
                      <FormDataConsumer>
                        {({
                          scopedFormData, // The data for this item of the ArrayInput
                          getSource, // Function to get the valid source inside an ArrayInput. Translates to "inventories.items[].property"
                          ...rest
                        }) => (
                          <Grid container direction="row" spacing={1}>
                            <Grid item xs={12} lg={3}>
                              <TextInput
                                source={getSource("name")}
                                label="Contact Name"
                                variant="outlined"
                                fullWidth
                                validate={validateRequired}
                                helperText={false}
                              />
                            </Grid>
                            <Grid item xs={12} lg={3}>
                              <TextInput
                                source={getSource("email")}
                                label="Email"
                                type="email"
                                variant="outlined"
                                fullWidth
                                parse={(v) =>
                                  v !== "" ? v.toLowerCase() : undefined
                                }
                                validate={validateOptEmail}
                                helperText={false}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextInput
                                source={getSource("phone")}
                                label="Phone"
                                variant="outlined"
                                fullWidth
                                validate={validateOptPhone}
                                helperText={false}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextInput
                                source={getSource("description")}
                                label="Description"
                                variant="outlined"
                                fullWidth
                                helperText={false}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </FormDataConsumer>
                    </SimpleFormIterator>
                  </ArrayInput>
                </fieldset>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </FormDataConsumer>
  );
};
